import React, { useState, useEffect } from 'react';
import EditModalWrapper from './EditModalWrapper';

const OCCUPATIONS = [
    'Nurse', 'Healthcare Assistant', 'Caregiver', 'Physiotherapist',
    'Medical Technician', 'Lab Technician', 'Pharmacist', 'Doctor',
    'IT Professional', 'Software Developer', 'Engineer', 'Teacher',
    'Chef', 'Hospitality', 'Sales', 'Marketing', 'Finance', 'HR',
    'Administrative', 'Other'
];

const AddEmploymentModal = ({
    isOpen,
    onClose,
    employmentData = null, // null for new, object for editing
    onSave,
    isAddMode = true
}) => {
    const buildForm = (data) => ({
        title: data?.title || '',
        companyName: data?.company_name || data?.company || '',
        description: data?.description || '',
        startDate: data?.start_date || '',
        endDate: data?.end_date || '',
        noEndDate: data?.no_end_date || data?.is_current || false,
        occupation: data?.occupation || '',
    });

    const [form, setForm] = useState(buildForm(employmentData));

    useEffect(() => {
        if (isOpen) {
            setForm(buildForm(employmentData));
        }
    }, [isOpen, employmentData]);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!form.title.trim()) {
            alert('Title is required');
            return;
        }
        if (!form.companyName.trim()) {
            alert('Company Name is required');
            return;
        }
        if (!form.startDate) {
            alert('Start Date is required');
            return;
        }
        if (!form.noEndDate && !form.endDate) {
            alert('End Date is required (or check "No End Date")');
            return;
        }

        setIsSubmitting(true);
        try {
            const employmentEntry = {
                title: form.title.trim(),
                company_name: form.companyName.trim(),
                company: form.companyName.trim(),
                description: form.description,
                start_date: form.startDate,
                end_date: form.noEndDate ? null : form.endDate,
                no_end_date: form.noEndDate,
                is_current: form.noEndDate,
                occupation: form.occupation,
            };

            await onSave(employmentEntry);
            onClose();
        } catch (error) {
            console.error('Error saving employment details:', error);
            alert('Failed to save changes. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <EditModalWrapper
            isOpen={isOpen}
            onClose={onClose}
            title={isAddMode ? "Add Employment Details" : "Edit Employment Details"}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            submitLabel={isAddMode ? "Add" : "Update"}
            size="md"
        >
            <div className="space-y-5">
                {/* Title */}
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        Title <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all bg-slate-50 text-slate-900"
                        placeholder="Enter"
                        required
                    />
                </div>

                {/* Company Name */}
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        Company Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={form.companyName}
                        onChange={(e) => setForm({ ...form, companyName: e.target.value })}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all bg-slate-50 text-slate-900"
                        placeholder="Enter"
                        required
                    />
                </div>

                {/* Description */}
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        Description
                    </label>
                    <textarea
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        rows={4}
                        maxLength={512}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all resize-none bg-slate-50 text-slate-900"
                        placeholder="Description"
                    />
                    <div className="text-right text-xs text-slate-400 mt-1">{512 - form.description.length}</div>
                </div>

                {/* Start Date & End Date */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            Start Date <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            value={form.startDate}
                            onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all text-slate-900"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            End Date <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            value={form.endDate}
                            onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                            disabled={form.noEndDate}
                            className={`w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all text-slate-900 ${form.noEndDate ? 'bg-slate-100 cursor-not-allowed' : ''}`}
                        />
                    </div>
                </div>

                {/* No End Date checkbox */}
                <div className="flex justify-end">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.noEndDate}
                            onChange={(e) => setForm({ ...form, noEndDate: e.target.checked, endDate: e.target.checked ? '' : form.endDate })}
                            className="h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                        />
                        <span className="text-sm text-slate-600">No End Date</span>
                        <span className="text-blue-500 text-sm">ⓘ</span>
                    </label>
                </div>

                {/* Occupation */}
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        Occupation
                    </label>
                    <select
                        value={form.occupation}
                        onChange={(e) => setForm({ ...form, occupation: e.target.value })}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all bg-slate-50 text-slate-900"
                    >
                        <option value="">Select</option>
                        {OCCUPATIONS.map((occ) => (
                            <option key={occ} value={occ}>{occ}</option>
                        ))}
                    </select>
                </div>
            </div>
        </EditModalWrapper>
    );
};

export default AddEmploymentModal;
